import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import NavBar from "@/components/NavBar";
import GolferTabs from "@/components/scoring/GolferTabs";
import GolferSelectionModal from "@/components/scoring/GolferSelectionModal";
import RecordBirdieModal from "@/components/birdie/RecordBirdieModal";
import { useGameData } from "@/hooks/useGameData";
import { useHoleScoring } from "@/hooks/useHoleScoring";
import { useRecurringInstances } from "@/hooks/useRecurringInstances";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import GameInfoCard from "@/components/game-detail/GameInfoCard";
import GameProgressBars from "@/components/game-detail/GameProgressBars";
import RecurringYearTabs from "@/components/game-detail/RecurringYearTabs";
import HoleGrid from "@/components/scoring/HoleGrid";
import GameActions from "@/components/game-detail/GameActions";
import { fetchAllTimeHoleTileData } from "@/integrations/supabase/scores/holeGridService";
import type { HoleTileData } from "@/integrations/supabase/scores";

const GameDetail = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();

  // Local active game ID — starts as URL gameId, changes on year tab switch.
  // useParams stays constant (we use replaceState, not navigate).
  const [activeGameId, setActiveGameId] = useState<string | undefined>(gameId);

  // Sync activeGameId if the user navigates here from a different page
  useEffect(() => {
    setActiveGameId(gameId);
    setOverrideTileData(undefined);
  }, [gameId]);

  // Game data loads once from the URL gameId (course + players stay the same across instances)
  const {
    selectedGolfers,
    selectedCourse,
    activeRound,
    isLoading
  } = useGameData(gameId);

  // Fetch recurring instances in parallel with game data (lightweight query)
  const {
    instances: recurringInstances,
    invalidateCache,
    getCachedTileData,
  } = useRecurringInstances(gameId);

  // Tile data override for instant tab switching (from prefetch cache)
  const [overrideTileData, setOverrideTileData] = useState<Map<number, HoleTileData> | undefined>(undefined);

  const {
    activeGolferId,
    setActiveGolferId,
    selectedHole,
    selectedGolfer,
    isRecordModalOpen,
    isGolferSelectOpen,
    refreshKey,
    holeTileData,
    lastFetchedGameId,
    handleHoleClick: baseHandleHoleClick,
    handleGolferSelect,
    handleBirdieRecorded: baseHandleBirdieRecorded,
    closeModal,
  } = useHoleScoring({
    golfers: selectedGolfers,
    course: selectedCourse,
    eligibleGames: activeRound?.eligibleGames || [],
    gameId: activeGameId,
    mode: 'game',
  });

  // Recurring game state
  const [isAllTime, setIsAllTime] = useState(false);
  const [allTimeHoleTileData, setAllTimeHoleTileData] = useState<Map<number, HoleTileData>>(new Map());

  // Derive currentGame: prefer recurring instances (has all years), fall back to activeRound
  const currentGame = recurringInstances.find(i => i.id === activeGameId)
    ?? activeRound?.eligibleGames?.find(g => g.id === activeGameId);

  // Clear tile data override when useHoleScoring completes fetch for the active game
  useEffect(() => {
    if (lastFetchedGameId === activeGameId) {
      setOverrideTileData(undefined);
    }
  }, [lastFetchedGameId, activeGameId]);

  // Handle year tab switching — no full page remount
  const handleSelectInstance = useCallback((newGameId: string) => {
    setActiveGameId(newGameId);
    setIsAllTime(false);

    // Instant display from prefetch cache (empty map on cache miss to avoid stale data)
    const cached = getCachedTileData(newGameId);
    setOverrideTileData(cached ?? new Map());

    // Update URL for sharing/refresh without triggering React Router remount
    window.history.replaceState(null, '', `/games/${newGameId}`);
  }, [getCachedTileData]);

  // Fetch all-time tile data when toggled
  useEffect(() => {
    if (!isAllTime || !selectedCourse || recurringInstances.length === 0) return;

    const load = async () => {
      const allGameIds = recurringInstances.map(i => i.id);
      const data = await fetchAllTimeHoleTileData({
        courseId: selectedCourse.id,
        gameIds: allGameIds,
      });
      setAllTimeHoleTileData(data);
    };
    load();
  }, [isAllTime, selectedCourse, recurringInstances, refreshKey]);

  const handleToggleAllTime = useCallback(() => {
    setIsAllTime(prev => !prev);
  }, []);

  // If no game ID is provided, navigate back to games list
  useEffect(() => {
    if (!gameId) {
      navigate("/games");
    }
  }, [gameId, navigate]);

  const handleBirdieRecorded = () => {
    baseHandleBirdieRecorded();
    // Invalidate prefetch cache for this game since data changed
    if (activeGameId) {
      invalidateCache(activeGameId);
    }
    toast({
      title: "Score recorded",
      description: "Your score has been successfully recorded."
    });
  };

  const handleBackToGames = () => {
    navigate('/games');
  };

  // All-Time view is read-only — no tile clicks
  const handleHoleClick = useCallback((holeNumber: number) => {
    if (isAllTime) return;
    baseHandleHoleClick(holeNumber);
  }, [isAllTime, baseHandleHoleClick]);

  // Show loading state while fetching game data
  if (isLoading || !selectedCourse || !currentGame) {
    return (
      <div className="min-h-screen pb-16">
        <Header title="Loading Game..." />
        <div className="px-4 pb-20 space-y-4 mt-2">
          {/* Game info skeleton */}
          <Card className="rounded-xl">
            <CardContent className="p-4 space-y-3">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-4 w-36" />
              <div className="flex gap-2 mt-2">
                {Array(3).fill(0).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-8 rounded-full" />
                ))}
              </div>
            </CardContent>
          </Card>
          {/* Progress skeleton */}
          <Card className="rounded-xl">
            <CardContent className="p-4 space-y-3">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-full rounded-full" />
              <Skeleton className="h-4 w-24 mt-2" />
              <Skeleton className="h-3 w-full rounded-full" />
            </CardContent>
          </Card>
          {/* Hole grid skeleton */}
          <div className="grid grid-cols-3 gap-2">
            {Array(9).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-16 rounded-lg" />
            ))}
          </div>
        </div>
        <NavBar />
      </div>
    );
  }

  // Use override (from cache) when available, otherwise fresh data from useHoleScoring
  const displayedHoleTileData = isAllTime
    ? allTimeHoleTileData
    : (overrideTileData !== undefined ? overrideTileData : holeTileData);
  const allTimeGameIds = recurringInstances.map(i => i.id);

  return (
    <div className="min-h-screen pb-16">
      <Header
        title={currentGame?.name || "Game Details"}
      />

      <div className="px-4 pb-20">
        {/* Game Info Card with Action Menu */}
        <GameInfoCard
          game={currentGame}
          course={selectedCourse}
          actions={<GameActions game={currentGame} onSuccess={() => navigate("/games")} />}
        />

        {/* Recurring Year Tabs */}
        {recurringInstances.length > 1 && activeGameId && (
          <RecurringYearTabs
            instances={recurringInstances}
            currentGameId={activeGameId}
            isAllTime={isAllTime}
            onToggleAllTime={handleToggleAllTime}
            onSelectInstance={handleSelectInstance}
          />
        )}

        {/* Progress Bars */}
        <GameProgressBars
          golfers={selectedGolfers}
          courseId={selectedCourse.id}
          gameId={activeGameId}
          gameIds={isAllTime ? allTimeGameIds : undefined}
          refreshKey={refreshKey}
        />

        {/* Golfer Tabs */}
        <GolferTabs
          golfers={selectedGolfers}
          selectedGolferId={activeGolferId}
          onGolferChange={setActiveGolferId}
        />

        {/* Hole Grid */}
        <div className="mb-6">
          <h3 className="font-display text-lg font-semibold text-forest mb-3">
            {isAllTime ? "All-Time Course Map" : "Course Map"}
          </h3>
          <HoleGrid
            holes={selectedCourse.holes}
            holeTileData={displayedHoleTileData}
            activeGolferId={activeGolferId}
            onHoleClick={handleHoleClick}
          />
        </div>

        {/* Back button */}
        <div className="flex justify-center">
          <button
            onClick={handleBackToGames}
            className="text-sm text-forest underline"
          >
            Back to Games
          </button>
        </div>
      </div>

      {/* Golfer Selection Modal — shown when "All" tab is active and a tile is clicked */}
      <GolferSelectionModal
        isOpen={isGolferSelectOpen}
        golfers={selectedGolfers}
        selectedHole={selectedHole}
        onGolferSelect={handleGolferSelect}
        onCancel={() => closeModal()}
      />

      {/* Birdie Record Modal */}
      {isRecordModalOpen && selectedGolfer && selectedHole && (
        <RecordBirdieModal
          isOpen={isRecordModalOpen}
          onClose={closeModal}
          onSuccess={handleBirdieRecorded}
          course={selectedCourse}
          userId={selectedGolfer.id}
          selectedHole={selectedHole}
          gameId={activeGameId}
          gameIds={recurringInstances.length > 1 ? recurringInstances.map(i => i.id) : [activeGameId || ""]}
          gamePlayers={selectedGolfers}
          mode="game"
        />
      )}

      <NavBar />
    </div>
  );
};

export default GameDetail;
